import { useTranslation } from 'react-i18next'
import { Card, CardBody, CardHeader } from '@structural-climbing/ui'
import { formatMoney } from './money'
import type { ProductSales } from './types'

interface Props {
  sales: ProductSales
  currency: string
  /** What the numbers cover, e.g. "This month". */
  periodLabel?: string
}

/**
 * Product sales for one period. The revenue here is already part of the gym's
 * revenue figure; this is the part of it that came from the shelf.
 */
export function ProductSalesCard({ sales, currency, periodLabel }: Props) {
  const { t } = useTranslation()
  // Profit is the owner's: the Worker sends null to everyone else.
  const showProfit = sales.profit !== null
  // Some lines have no buying price, so the profit covers only part of the
  // revenue. Saying which part stops it being read as the margin on all of it.
  const partial =
    showProfit && sales.revenueWithCost !== null && sales.revenueWithCost < sales.revenue

  return (
    <Card>
      <CardHeader className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t('products.salesTitle')}</h2>
        {periodLabel && <p className="text-xs text-foreground/50">{periodLabel}</p>}
      </CardHeader>
      <CardBody className="gap-4 pt-0">
        {sales.units === 0 ? (
          <p className="py-4 text-center text-sm text-foreground/50">{t('products.salesEmpty')}</p>
        ) : (
          <>
            <div className={`grid gap-3 ${showProfit ? 'grid-cols-3' : 'grid-cols-2'}`}>
              <div>
                <p className="text-xs text-foreground/50">{t('products.salesRevenue')}</p>
                <p className="text-lg font-bold tabular-nums">
                  {formatMoney(sales.revenue, currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-foreground/50">{t('products.salesUnits')}</p>
                <p className="text-lg font-bold tabular-nums">{sales.units}</p>
              </div>
              {showProfit && (
                <div>
                  <p className="text-xs text-foreground/50">{t('products.salesProfit')}</p>
                  <p className="text-lg font-bold tabular-nums">
                    {formatMoney(sales.profit as number, currency)}
                  </p>
                </div>
              )}
            </div>
            {partial && (
              <p className="-mt-2 text-xs text-foreground/50">
                {t('products.salesProfitPartial', {
                  revenue: formatMoney(sales.revenueWithCost as number, currency),
                })}
              </p>
            )}

            <div>
              <p className="mb-1 text-xs font-medium text-foreground/60">
                {t('products.topSellers')}
              </p>
              <div className="flex flex-col divide-y divide-divider">
                {sales.top.map((row) => (
                  <div key={row.name} className="flex items-center justify-between gap-3 py-1.5">
                    <p className="min-w-0 truncate text-sm">{row.name}</p>
                    <p className="shrink-0 text-xs text-foreground/50 tabular-nums">
                      {t('products.unitsSold', { n: row.units })} ·{' '}
                      {formatMoney(row.revenue, currency)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  )
}
